import React, { useState, useEffect, useRef, useMemo } from 'react';
import Menu from '../../../../components/layout/menu';
import { axiosInstance } from '../../../../client';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Types, IconTypes, VALID_TYPES } from '../../../../constants/announcementTypes';
import { downloadExcel } from '../../../../utils/xlsxUtils';
import Tabs from '../../../../components/announcements/Tabs';
import CommonFields from '../../../../components/announcements/CommonFields';
import DropArea from '../../../../components/announcements/DropArea';
import RecentManuals from '../../../../components/announcements/RecentManuals';
import Modals from '../../../../components/announcements/Modals';
import dangerIcon from "../../../../assets/images/icons/announcements/danger.svg";
import alertIcon from "../../../../assets/images/icons/announcements/alert.svg";
import checkIcon from "../../../../assets/images/icons/announcements/check.svg";
import formIcon from "../../../../assets/images/icons/announcements/form.svg";
import { useAnnouncementValidation } from '../../../../hooks/announcements/useAnnouncementValidation';
import UploadDocumentButton from '../../../../components/shared/uploadDocument';
import DownloadDocumentButton from '../../../../components/shared/downloadDocument';

export default function AnnouncementsPublish() {
  const today = new Date().toLocaleDateString('en-CA', {
    timeZone: 'America/Lima'
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const [activeTab, setActiveTab] = useState(Types.Image);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const excelInputRef = useRef(null);
  const dropInputRef = useRef(null);
  const [manuals, setManuals] = useState([]);
  const [selectedManualId, setSelectedManualId] = useState(null);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [editorText, setEditorText] = useState('');
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [suppliers, setSuppliers] = useState([]);
  const [notFound, setNotFound] = useState([]);
  const [flaggedSuppliers, setFlaggedSuppliers] = useState([]);
  const [maxExceededSuppliers, setMaxExceededSuppliers] = useState([]);

  const [showNotFound, setShowNotFound] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [showMaxExceeded, setShowMaxExceeded] = useState(false);
  const [loading, setLoading] = useState(false);
const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);
  const values = {
    activeTab,
    selectedManualId,
    selectedFile,
    title,
    summary,
    selectedIcon,
    editorText,
    fromDate,
    toDate,
    suppliers
  };

  const { isValid, errors } = useAnnouncementValidation(values);


  useEffect(() => {
    if (activeTab === Types.Recents) fetchManuals();
  }, [activeTab]);

const quillRef = useRef(null); 

const handleKeyDown = (e) => {
  if (activeTab === Types.Advertisement && characterCount >= 500 && 
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key) &&
      !e.ctrlKey && !e.metaKey) {
    e.preventDefault();
  }
};


  const fetchManuals = async () => {
    setLoading(true);
    setShowError(false);
    try {
      const res = await axiosInstance.get('/Manual/ListManuals');
      setManuals(res.data.manuals);
    } catch {
      setErrorMessage('No se pudieron cargar los manuales.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const preventDefaults = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = e => {
    preventDefaults(e);
    const file = e.dataTransfer.files[0];
    handleFileChange(file);
  };

  const handleFileChange = file => {
    if (!file) return;

    const allowed = VALID_TYPES[activeTab] || [];

    if (!allowed.includes(file.type)) {
      let formatErrorMessage = '';
      
      switch(activeTab) {
        case Types.Image:
          formatErrorMessage = 'Formato no permitido. Solo se permiten archivos de imagen: JPG, JPEG, PNG, WEBP o SVG.';
          break;
        case Types.Pdf:
          formatErrorMessage = 'Formato no permitido. Solo se permiten archivos PDF.';
          break;
        case Types.Video:
          formatErrorMessage = 'Formato no permitido. Solo se permiten archivos de video: GIF, MOV o MP4.';
          break;
        default:
          formatErrorMessage = 'Formato no permitido.';
      }
      
      setErrorMessage(formatErrorMessage);
      setShowError(true);
      if (dropInputRef.current) dropInputRef.current.value = '';
      return;
    }

    const maxSize = 50 * 1024 * 1024; 
    if (file.size > maxSize) {
      let sizeErrorMessage = '';
      
      switch(activeTab) {
        case Types.Image:
          sizeErrorMessage = 'Tamaño no permitido. Solo se permiten archivos de imagen de hasta 50 MB como máximo.';
          break;
        case Types.Pdf:
          sizeErrorMessage = 'Tamaño no permitido. Solo se permiten archivos PDF de hasta 50 MB como máximo.';
          break;
        case Types.Video:
          sizeErrorMessage = 'Tamaño no permitido. Solo se permiten archivos de video de hasta 50 MB como máximo.';
          break;
        default:
          sizeErrorMessage = 'Tamaño no permitido. El archivo excede el límite de 50 MB.';
      }
      
      setErrorMessage(sizeErrorMessage);
      setShowError(true);
      if (dropInputRef.current) dropInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
  };

const handleExcelChange = async e => {
  const file = e.target.files[0];
  if (!file) return;

  const validTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.oasis.opendocument.spreadsheet'
  ];
  const validExts = ['xls', 'xlsx', 'ods'];
  const ext = file.name.split('.').pop().toLowerCase();

  if (
    !validTypes.includes(file.type) &&
    !validExts.includes(ext)
  ) {
    setErrorMessage('Formato no válido. Usa XLS, XLSX u ODS.');
    setShowError(true);
    if (excelInputRef.current) excelInputRef.current.value = '';
    return;
  }

  setLoading(true);
  
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosInstance.post('/Advertisement/UploadSuppliers', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    const { validSuppliers, invalidSuppliers } = response.data;

    setSuppliers(validSuppliers.map(s => ({
      id: s.id,
      name: s.name,
      code: s.code
    })));

    if (invalidSuppliers && invalidSuppliers.length > 0) {
      const invalidCodes = invalidSuppliers.map(s => s.code);
      setNotFound(invalidCodes);
      setShowNotFound(true);
    }

    if (validSuppliers.length > 0 && (!invalidSuppliers || invalidSuppliers.length === 0)) {
      setSuccessMessage(`Se cargaron ${validSuppliers.length} proveedores correctamente.`);
      setShowSuccess(true);
    }

  } catch (err) {
    console.error('Error al procesar el archivo:', err);
    
    if (err.response && err.response.data && err.response.data.message) {
      setErrorMessage(err.response.data.message);
    } else {
      setErrorMessage('Error al procesar el archivo. Por favor, verifica el formato.');
    }
    setShowError(true);
  } finally {
    setLoading(false);
    if (excelInputRef.current) excelInputRef.current.value = '';
  }
};

  const handlePublish = async () => {
  if (activeTab === Types.Advertisement && characterCount > 500) {
    setErrorMessage(`Has excedido el límite de 500 caracteres. Tienes ${characterCount} caracteres.`);
    setShowError(true);
    return;
  }
  
    if (!isValid) {
      const firstError = errors[Object.keys(errors)[0]];
      setErrorMessage(firstError);
      setShowError(true);
      return;
    }

    setLoading(true);
    const { data: counts } = await axiosInstance.post('/Advertisement/CheckSupplierAdCounts', suppliers.map(s => s.code));
    const exceeded = counts.filter(c => c.activeAdsCount >= 5);
    if (exceeded.length) {
      setMaxExceededSuppliers(exceeded);
      setShowMaxExceeded(true);
      setLoading(false);
      return;
    }
    const flagged = counts.filter(c => c.activeAdsCount >= 3);
    if (flagged.length) {
      setFlaggedSuppliers(flagged);
      setShowConfirm(true);
      setLoading(false);
      return;
    }
    await doPublish();
  };

  useEffect(() => {
  if (activeTab === Types.Advertisement && editorText) {
    const textOnly = editorText.replace(/<[^>]*>/g, '');
    setCharacterCount(textOnly.length);
  } else {
    setCharacterCount(0);
  }
}, [editorText, activeTab]);

function utf8ToBase64(str) {
    try {
        // Codificar el string a UTF-8
        const encoder = new TextEncoder();
        const encoded = encoder.encode(str);
        
        // Convertir Uint8Array a string binaria
        let binaryString = '';
        for (let i = 0; i < encoded.length; i++) {
            binaryString += String.fromCharCode(encoded[i]);
        }
        
        // Codificar a Base64
        return btoa(binaryString);
    } catch (error) {
        console.error('Error codificando a Base64:', error);
        // Fallback: usar encodeURIComponent si hay error
        return btoa(encodeURIComponent(str));
    }
}

  const doPublish = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('typeId', activeTab);
      
      if (activeTab === Types.Recents) {
        formData.append("manualId", selectedManualId);
      }
      else if (
        activeTab === Types.Image ||
        activeTab === Types.Pdf ||
        activeTab === Types.Video
      ) {
        formData.append("file", selectedFile);
        formData.append("title", title);
        formData.append("summary", summary);
      }
      else if (activeTab === Types.Advertisement) {
        formData.append("iconId", selectedIcon);
        formData.append("description", utf8ToBase64(editorText));
      }

      formData.append("fromDate", fromDate);
      formData.append("toDate", toDate);
      formData.append(
        "suppliers",
        JSON.stringify(suppliers.map((s) => s.code))
      );
      await axiosInstance.post('/Advertisement', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccessMessage('¡Publicación realizada con éxito!');
      setShowSuccess(true);
      resetForm();
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      setErrorMessage('No se pudo publicar, intenta de nuevo.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async (e) => {
    e.preventDefault();

    try {
      setDownloadingTemplate(true);
      const response = await axiosInstance.get('/Advertisement/ExportTemplate', {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'Plantilla_de_proveedores.xlsx';

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          fileName = fileNameMatch[1];
        }
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      setErrorMessage('No se pudo descargar el archivo, intenta de nuevo.');
      setShowError(true);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const resetForm = () => {
    URL.revokeObjectURL(previewUrl || '');
    setPreviewUrl(null);
    setTitle('');
    setSummary('');
    setSelectedFile(null);
    setSelectedIcon(null);
    setEditorText('');
    setCharacterCount(0);
    setSelectedManualId(null);
    setSuppliers([]);
    setNotFound([]);
    setFromDate(today);
    setToDate(today);
    excelInputRef.current && (excelInputRef.current.value = '');
    dropInputRef.current && (dropInputRef.current.value = '');
  };

  const handleDownloadFlagged = () => downloadExcel('proveedores_excedidos.xlsx', [
    ['Código', 'Razón Social', 'Cantidad de Anuncios Activos'],
    ...flaggedSuppliers.map(s => [s.supplierCode, s.supplierName, s.activeAdsCount])
  ]);

  const handleDownloadMaxExceeded = () => downloadExcel('proveedores_excedidos.xlsx', [
    ['Código', 'Razón Social', 'Cantidad de Anuncios Activos'],
    ...maxExceededSuppliers.map(s => [s.supplierCode, s.supplierName, s.activeAdsCount])
  ]);

  /* const modules = useMemo(() => ({
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ header: 1 }, { header: 2 }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["clean"],
    ],
    clipboard: {
      matchers: [
        ["A", (node, delta) => {
          return delta.compose(new window.Quill.import("delta")().retain(delta.length(), { link: false }));
        }],
      ],
    },
  }), []);

  const formats = [
    "bold", "italic", "underline", "strike",
    "header", "list", "bullet", "align",
  ]; */

  return (
    <div>
      <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
      <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"} bg-[#F7F8F9] w-full z-50 min-h-screen`}>
        <div className="sm:pt-8 pt-5 pb-10 sm:px-[3%] px-[5%] flex flex-col gap-2">
          <h1 className="  text-2xl md:text-[32px] text-blue font-itcbold">Anuncios</h1>
          <p className="text-[#6B7280] font-itcbook text-lg md:text-xl leading-snug">
            Aquí podrás administrar tus publicaciones que se verán cuando el proveedor ingrese al sistema.
          </p>
          <h2 className="text-base font-itcmedium text-[#2F2F3B] mt-2">
            Publicar:
          </h2>
          <div className="mt-3 overflow-y-auto overflow">
            <Tabs activeTab={activeTab} onTabChange={tab => { setActiveTab(tab); resetForm(); }} />
          </div>
          <div className="mt-1">
            {activeTab === Types.Image && (
              <>
                <p className="text-[#6B7280] pb-2 font-itcbook text-base leading-snug mb-1">
                  Recuerda usar formatos como JPG, JPEG, PNG, WEBP o SVG (máx. 50 MB)
                </p>
                <DropArea
                  accept={VALID_TYPES[activeTab].join(',')}
                  selectedFile={selectedFile}
                  onDrop={handleDrop}
                  onChange={e => handleFileChange(e.target.files[0])}
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h2l2-3h10l2 3h2a1 1 0 011 1v11a1 1 0 01-1 1H3a1 1 0 01-1-1V8a1 1 0 011-1zm9 3a3 3 0 100 6 3 3 0 000-6z" />
                    </svg>
                  }
                  text="Arrastra o haz clic para cargar una imagen"
                  preventDefaults={preventDefaults}
                  previewUrl={previewUrl}
                  setPreviewUrl={setPreviewUrl}
                  inputRef={dropInputRef}
                />
                <CommonFields title={title} setTitle={setTitle} summary={summary} setSummary={setSummary} />
              </>
            )}
            {activeTab === Types.Pdf && (
              <>
                <p className="text-[#6B7280] text-base font-itcbook  leading-snug mb-1">
                  Recuerda usar solo el formato PDF (máx. 50 MB)
                </p>
                <DropArea
                  accept={VALID_TYPES[activeTab].join(',')}
                  selectedFile={selectedFile}
                  onDrop={handleDrop}
                  onChange={e => handleFileChange(e.target.files[0])}
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 2v6h6" />
                    </svg>
                  }
                  text="Arrastra o haz clic para cargar un archivo PDF"
                  preventDefaults={preventDefaults}
                  previewUrl={previewUrl}
                  setPreviewUrl={setPreviewUrl}
                  inputRef={dropInputRef}
                />
                <CommonFields title={title} setTitle={setTitle} summary={summary} setSummary={setSummary} />
              </>
            )}
            {activeTab === Types.Advertisement && (
              <div className="grid grid-cols-1 gap-4 lg:gap-6 sm:grid-cols-6">
                <div className="sm:col-span-2 md:col-span-1">
                  <p className="text-[#6B7280] font-itcbook text-base leading-snug mb-1">
                    Escoge un ícono: <span className="text-red-500">*</span>
                  </p>
                  <div className="flex items-center justify-center p-4 bg-white rounded shadow h-80">
                    <div className="flex flex-col items-center gap-4">
                      {[
                        { key: IconTypes.Danger, src: dangerIcon, alt: "Peligro" },
                        { key: IconTypes.Alert, src: alertIcon, alt: "Alerta" },
                        { key: IconTypes.Check, src: checkIcon, alt: "Correcto" },
                        { key: IconTypes.Form, src: formIcon, alt: "Formulario" },
                      ].map((icon) => (
                        <div
                          key={icon.key}
                          onClick={() => setSelectedIcon(icon.key)}
                          className="relative cursor-pointer"
                        >
                          <img src={icon.src} alt={icon.alt} className="relative z-10 w-12 h-12" />
                          {selectedIcon === icon.key && (
                            <div className="absolute top-0 left-0 w-full h-full bg-[#abdaf1] opacity-60 rounded z-20" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="col-span-1 sm:col-span-4 md:col-span-5">
                  <div className='flex items-center justify-between gap-2 mb-1'>
                    <p className="text-[#6B7280] font-itcbook text-base leading-snug ">
                      Escribe tu texto aqui: <span className="text-red-500">*</span>
                    </p>
                    <p className={`text-xs font-itcbook ${characterCount > 500 ? 'text-red-600' : 'text-[#6B7280]'}`}>
                      {characterCount}/500 caracteres
                    </p>
                  </div>
                  <div>

                  <style>
                      {`
                        .ql-container {
                          height: 278px !important;
                          min-height: 278x !important;
                          border-bottom-left-radius: 4px !important;
                          border-bottom-right-radius: 4px !important;
                          background-color: white;
                      }

                      .ql-editor {
                        min-height: 278px !important;
                        max-height: 278px !important;

                      }
                      .ql-toolbar {
                        border-top-right-radius: 4px !important;
                        border-top-left-radius: 4px !important;
                        background-color: white;
                      }
                      .quill {
                        border-radius: 4px !important;
                        --tw-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
                        --tw-shadow-colored: 0 1px 3px 0 var(--tw-shadow-color), 0 1px 2px -1px var(--tw-shadow-color);
                        box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow) !important;
                      }
                      `}
                  </style>
<div onKeyDown={handleKeyDown}>
  <ReactQuill
    ref={quillRef}
    theme="snow"
    value={editorText}
    onChange={(content) => {
      const textOnly = content.replace(/<[^>]*>/g, '');
      if (textOnly.length <= 500) {
        setEditorText(content);
        setCharacterCount(textOnly.length);
      }
    }}
    className="bg-white"
  />
</div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === Types.Video && (
              <>
                <p className="text-[#6B7280] mb-2 font-itcbook text-base leading-snug ">
                  Recuerda usar formatos como GIF, MOV o MP4 (máx. 50 MB)
                </p>
                <DropArea
                  accept={VALID_TYPES[activeTab].join(',')}
                  selectedFile={selectedFile}
                  onDrop={handleDrop}
                  onChange={e => handleFileChange(e.target.files[0])}
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 32 32"
                      className="w-12 h-12 mb-2"
                      fill="none"
                    >
                      <polygon
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeMiterlimit="10"
                        points="7,28 7,4 19,4 25,10 25,28"
                      />
                      <polyline
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeMiterlimit="10"
                        points="19,4 19,10 25,10"
                      />
                      <polygon
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeMiterlimit="10"
                        points="19,18 14,21 14,15"
                      />
                    </svg>
                  }
                  text="Arrastra o haz clic para cargar un video"
                  preventDefaults={preventDefaults}
                  previewUrl={previewUrl}
                  setPreviewUrl={setPreviewUrl}
                  inputRef={dropInputRef}
                />
                <CommonFields title={title} setTitle={setTitle} summary={summary} setSummary={setSummary} />
              </>
            )}
            {activeTab === Types.Recents && (
              <RecentManuals manuals={manuals} selectedId={selectedManualId} onSelect={setSelectedManualId} />
            )}
          </div>

          <div className="mt-4">
            <label className="block text-sm sm:text-base font-itcmedium text-[#6B7280] mb-2">Tiempo de publicación</label>
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex items-center flex-1 gap-2">
                <label className="whitespace-nowrap font-itcbook text-sm text-[#6B7280]">Desde</label>
                <input
                  type="date"
                  min={today}
                  value={fromDate}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md font-itcbook focus:outline-none focus:ring-2 focus:ring-blue"
                  onChange={e => setFromDate(e.target.value)}
                />
              </div>
              <div className="flex items-center flex-1 gap-2">
                <label className="whitespace-nowrap font-itcbook text-sm text-[#6B7280]">Hasta</label>
                <input
                  type="date"
                  min={fromDate}
                  value={toDate}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md font-itc-book focus:outline-none focus:ring-2 focus:ring-blue"
                  onChange={e => setToDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className='flex flex-col items-start w-full gap-3 py-3'>
            <UploadDocumentButton 
              text="Subir lista de proveedores"
              isEnabled={true}
              onClick={() => excelInputRef.current?.click()}
            />
            <input
              type="file"
              accept=".xlsx,.xls,.ods"
              className="hidden"
              ref={excelInputRef}
              onChange={handleExcelChange}
            />
            <DownloadDocumentButton 
              text="Descargar plantilla"
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
              loading={downloadingTemplate}
              hasData={true}
            />
            
          </div>



          {suppliers.length > 0 && (
            <div className="p-4 bg-white border border-gray-200 rounded-md shadow">
              <h4 className="mb-2 font-itcdemi text-blue">Proveedores:</h4>
              <ul className="text-gray-700 list-disc list-inside">
                {suppliers.map((sup) => (
                  <li key={sup.code}>{sup.name} (Código: {sup.code})</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-center gap-4">
            <button
              onClick={handlePublish}
              className="px-8 py-2 text-sm text-white transition rounded-lg md:text-lg font-itcmedium bg-green"
            >
              Publicar
            </button>
            <button
              onClick={resetForm}
              className="px-8 py-2 text-sm transition border-2 rounded-lg md:text-lg font-itcmedium border-green text-green"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-60">
          <svg className="w-10 h-10 text-blue-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      )}

      <Modals
        showNotFound={showNotFound} notFound={notFound} onCloseNotFound={() => setShowNotFound(false)}
        showError={showError} errorMessage={errorMessage} onCloseError={() => setShowError(false)}
        showSuccess={showSuccess} successMessage={successMessage} onCloseSuccess={() => setShowSuccess(false)}
        showConfirm={showConfirm} flaggedSuppliers={flaggedSuppliers} onConfirm={() => { setShowConfirm(false); doPublish(); }} onDownloadFlagged={handleDownloadFlagged} onCloseConfirm={() => setShowConfirm(false)}
        showMaxExceeded={showMaxExceeded} maxExceededSuppliers={maxExceededSuppliers} onDownloadMaxExceeded={handleDownloadMaxExceeded} onCloseMaxExceeded={() => setShowMaxExceeded(false)}
      />
    </div>
  );
}